/** TanStack Query hooks for every API read. */
import type { ChartRange, FiatCurrency, KeyFamily } from "@custos/api-client";
import { useQuery } from "@tanstack/react-query";
import { api } from "./api";

export const qk = {
  config: ["config"] as const,
  messages: ["messages"] as const,
  publicMessages: ["public-messages"] as const,
  chat: ["chat"] as const,
  chatUnread: ["chat-unread"] as const,
  me: ["me"] as const,
  balances: ["balances"] as const,
  deposits: ["deposits"] as const,
  withdrawals: ["withdrawals"] as const,
  whitelist: ["whitelist"] as const,
  sessions: ["sessions"] as const,
  portfolio: (currency: FiatCurrency) => ["portfolio", currency] as const,
  portfolioHistory: (currency: FiatCurrency, range: ChartRange) =>
    ["portfolio", currency, "history", range] as const,
  swapOptions: ["swap-options"] as const,
  swaps: ["swaps"] as const,
  depositAddress: (family: KeyFamily) => ["deposit-address", family] as const,
  vaultBalances: (currency: FiatCurrency, addresses: string) =>
    ["vault-balances", currency, addresses] as const,
};

/** Public: network mode, chains, assets. Changes only on a server update. */
export const useConfig = () =>
  useQuery({ queryKey: qk.config, queryFn: () => api.config(), staleTime: Infinity });

/** Messages from the team (D78): checked every minute and when the tab is focused again (D75). */
export const useMessages = () =>
  useQuery({
    queryKey: qk.messages,
    queryFn: () => api.messages(),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
/** Unread support replies for the chat button (D79): every 30 s. */
export const useChatUnread = () =>
  useQuery({ queryKey: qk.chatUnread, queryFn: () => api.chatUnread(), refetchInterval: 30_000 });
export const usePublicMessages = () =>
  useQuery({ queryKey: qk.publicMessages, queryFn: () => api.publicMessages(), refetchInterval: 60_000 });

export const useMe = () => useQuery({ queryKey: qk.me, queryFn: () => api.me() });

export const useBalances = () =>
  useQuery({ queryKey: qk.balances, queryFn: () => api.balances(), refetchInterval: 30_000 });

export const useDeposits = () =>
  useQuery({ queryKey: qk.deposits, queryFn: () => api.deposits(), refetchInterval: 30_000 });

export const useWithdrawals = () =>
  useQuery({ queryKey: qk.withdrawals, queryFn: () => api.withdrawals(), refetchInterval: 20_000 });

export const useWhitelist = () => useQuery({ queryKey: qk.whitelist, queryFn: () => api.whitelist() });

export const useSessions = () => useQuery({ queryKey: qk.sessions, queryFn: () => api.sessions() });

/** The user's deposit address for a chain family (created on first use; never changes). */
export const useDepositAddress = (family: KeyFamily | null) =>
  useQuery({
    queryKey: qk.depositAddress(family ?? "EVM"),
    queryFn: () => api.depositAddress(family!),
    enabled: family !== null,
    staleTime: Infinity,
  });

/** Balances valued in the display currency (prices refresh every minute on the server). */
export const usePortfolio = (currency: FiatCurrency) =>
  useQuery({
    queryKey: qk.portfolio(currency),
    queryFn: () => api.portfolio(currency),
    refetchInterval: 60_000,
  });

export const usePortfolioHistory = (currency: FiatCurrency, range: ChartRange) =>
  useQuery({
    queryKey: qk.portfolioHistory(currency, range),
    queryFn: () => api.portfolioHistory(currency, range),
    refetchInterval: 5 * 60_000,
    placeholderData: (previous) => previous,
  });

export const useSwapOptions = () => useQuery({ queryKey: qk.swapOptions, queryFn: () => api.swapOptions() });

export const useSwaps = () =>
  useQuery({ queryKey: qk.swaps, queryFn: () => api.swaps(), refetchInterval: 30_000 });

/**
 * On-chain balances of the unlocked vault. Sends PUBLIC addresses only (D60); the query is
 * removed from the cache when the vault locks.
 */
export const useVaultBalances = (
  accounts: { family: KeyFamily; address: string }[],
  currency: FiatCurrency,
  enabled: boolean,
) => {
  const addresses = Object.fromEntries(accounts.map((a) => [a.family, a.address])) as Partial<
    Record<KeyFamily, string>
  >;
  return useQuery({
    queryKey: qk.vaultBalances(currency, JSON.stringify(addresses)),
    queryFn: () => api.vaultBalances(addresses, currency),
    enabled: enabled && accounts.length > 0,
    refetchInterval: 30_000,
  });
};

/** Vault recovery status for this account (backup on/off, open request). */
export const useVaultRecovery = () =>
  useQuery({ queryKey: ["vault-recovery"], queryFn: () => api.vaultRecovery(), refetchInterval: 60_000 });
